import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { CheckoutStorageService } from '@bytel/checkout';

import { Observable, ReplaySubject, Subject, of } from 'rxjs';

import { BasicObject } from '../../../interfaces/checkout.interface';
import { StepMock } from '../../../interfaces/mock.interface';

interface StepConstructor {
    key: string;
    new (): Step;
}

export abstract class Step {
    public isValid: boolean = false;
    public isBypass: boolean = false;
    public key: string;
    public order: number;
    public previous: Step;
    public icon: string = null;
    public stepMocks: StepMock[] = null;
    public ecommerceStep: string = 'Checkout';
    public needStepperRefresh: Observable<BasicObject>;
    public isLoaded: Observable<boolean>;
    public shortLabel: string;
    protected _needStepperRefresh: Subject<BasicObject> = new Subject();
    protected _isLoaded: ReplaySubject<boolean> = new ReplaySubject(1);
    protected router = inject(Router);
    protected checkoutStorage = inject(CheckoutStorageService);
    private _storageKey: string = 'steps';
    abstract label: string;
    abstract next: Step;
    abstract routerLink: string;
    abstract hasReminder: boolean;

    protected constructor() {
        const stepKey = (this.constructor as StepConstructor).key;
        this.key = stepKey;
        this.setStepFromStorage();
        this.needStepperRefresh = this._needStepperRefresh.asObservable();
        this.isLoaded = this._isLoaded.asObservable();
    }

    public get isActive(): boolean {
        const link = this.routerLink || '/';
        return link === this.router.url.split('?')[0];
    }

    // public canGoToNextStep(currentRouterLink: string): Observable<boolean> {
    //     const canGo = of(currentRouterLink !== this.routerLink && this.isValid && this.next);
    //     return canGo.pipe(
    //         mergeMap(res => res ? this.next.canAccess() : of(false))
    //     );
    // }

    public navigateToStep(currentRouterLink: string): boolean {
        if (currentRouterLink.includes(this.routerLink)) {
            return true;
        }

        this.router.navigateByUrl(this.routerLink);
    }

    public setByPass(): void {
        this.isBypass = false;
    }

    public bypass(): Observable<boolean> {
        return of(true);
    }

    public nextStep(): Step {
        const currentSteps: BasicObject = this.checkoutStorage.get(this._storageKey) || {};
        currentSteps[this.key] = this._extractStepToSaveToStorage();
        this.checkoutStorage.set(this._storageKey, currentSteps);

        const nextStep: Step = this.next;
        if (this.isValid && nextStep) {
            return nextStep;
        } else {
            return this;
        }
    }

    public getNextStep(): Step {
        const nextStep: Step = this.next;
        if (nextStep && !nextStep.isBypass) {
            return nextStep;
        } else if (nextStep.isBypass) {
            return nextStep.next;
        } else {
            return this;
        }
    }

    public previousStep(): void {
        if (this.previous) {
            this.router.navigateByUrl(this.previous.routerLink);
        }
    }

    public saveToStorage(): void {
        const currentSteps: BasicObject = this.checkoutStorage.get(this._storageKey) || {};
        currentSteps[this.key] = this._extractStepToSaveToStorage();
        this.checkoutStorage.set(this._storageKey, currentSteps);
    }

    public setCurrentStep(): void {
        this.router.navigateByUrl(this.routerLink);
    }

    public setStepFromStorage(): void {
        const currentSteps: BasicObject = this.checkoutStorage.get(this._storageKey) || {};
        this.isValid = !!currentSteps[this.key]?.isValid;
    }

    public forceReminder(): boolean {
        return false;
    }

    public getReminderData(): string {
        return null;
    }

    public getReminderDetailBlocData(): BasicObject[] {
        return null;
    }

    public getReminderHtmlDetail(): string {
        return null;
    }

    public isEnable(): Observable<boolean> {
        return of(this.canDisplay());
    }

    private _extractStepToSaveToStorage(): { isValid: boolean } {
        return {
            isValid: this.isValid,
        };
    }

    abstract canAccess(): Observable<boolean>;

    abstract canDisplay(): boolean;
}
